import { getLogger, type Logger } from "@expert/logging";
import { createSimpleStore } from "@expert/state-utils";

import type { SummaryResolution } from "@/shared-types";

import { useSessionStore } from "@/sdk/sessions/session.store";

const logger = getLogger({
    module: "useSessionStore",
});

export type SessionSummary = string;

export interface SetSessionSummaryPayload {
    value: SessionSummary;
    autoGenerated?: boolean;
}

export interface SessionSummaryState {
    summary: SessionSummary;
    autogeneratedSummary: SessionSummary;
    resolutionStatus: SummaryResolution;
    /** true if resolution status form should be hidden from the summary modal */
    hideResolutionStatusForm: boolean;
    submitted: boolean;
    canAppend: boolean;
    customerEndedCall: boolean;
    setCanAppend: (value: boolean) => void;
    setSessionSummary: (payload: SetSessionSummaryPayload) => void;
    setSubject: (subject: string) => void;
    subject: string;
    setCategory: (category: string | undefined) => void;
    category: string | undefined;
    setSubcategory: (subcategory: string | undefined) => void;
    subcategory: string | undefined;
    appendSessionSummary: (fragment: SessionSummary) => void;
    /** reset state to default */
    resetSessionSummary: () => void;
    /** change resolution status to predefined value. Optionally the form can be hidden from the summary modal when calling this */
    setResolved: (wasIssueResolved: SummaryResolution, options?: { hideResolutionStatusForm?: boolean }) => void;
    setSubmitted: (value: boolean) => void;
    setCustomerEndedCall: (value: boolean, logger: Logger) => void;
}

const INITIAL_RESET_STATE = {
    summary: "",
    autogeneratedSummary: "",
    resolutionStatus: null as SummaryResolution,
    hideResolutionStatusForm: false,
    canAppend: true,
    subject: "",
    category: undefined as string | undefined,
    subcategory: undefined as string | undefined,
    submitted: false,
    customerEndedCall: false,
};

/**
 * Session summary text, resolution status, subject/category metadata, and submission flags
 * collected during the wrap-up flow. Reset between sessions via `resetSessionSummary`, and
 * subscribes to `useSessionStore` to clear `resolutionStatus` when a new task is added mid-session.
 */
export const useSessionSummaryStore = createSimpleStore<SessionSummaryState>({
    devtools: {
        store: "session-summary",
        name: "workspace/session-summary",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
    },
})((set, get) => ({
    ...INITIAL_RESET_STATE,
    setCanAppend: (canAppend) => set({ canAppend }),
    setSessionSummary: ({ value, autoGenerated }) =>
        set(autoGenerated ? { summary: value, autogeneratedSummary: value } : { summary: value }),
    setSubject: (subject) => set({ subject }),
    setCategory: (category) => set({ category }),
    setSubcategory: (subcategory) => set({ subcategory }),
    setCustomerEndedCall: (customerEndedCall, customLogger) => {
        customLogger.info({ customerEndedcall: customerEndedCall }, "Customer ended call.");
        set({ customerEndedCall });
    },
    appendSessionSummary: (fragment) => {
        const { canAppend, summary } = get();
        if (!canAppend) {
            logger.warn("Cannot append to session summary, canAppend is false");
            return;
        }
        const formatted = [summary, fragment].join(" ").trim();
        set({ summary: formatted, autogeneratedSummary: formatted });
    },
    resetSessionSummary: () => set(INITIAL_RESET_STATE),
    setResolved: (wasIssueResolved, options = { hideResolutionStatusForm: false }) =>
        set({ resolutionStatus: wasIssueResolved, hideResolutionStatusForm: options.hideResolutionStatusForm }),
    setSubmitted: (submitted) => set({ submitted }),
}));

useSessionStore.subscribe(({ sessions }, { sessions: prevSessions }) => {
    // if a task was added to any sessions then any previously declared summary resolution should be cleared
    const taskAdded = sessions.some((session, index) => session?.tasks.size > prevSessions[index]?.tasks.size);
    if (taskAdded) {
        useSessionSummaryStore.getState().setResolved(null);
    }
});
